import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import useApiManager from '../../utils/Api/apiManager';

const DealsAutoComplete = ({ setDealId, dealId, shouldFetchSingleDeal }) => {
    const [dealOptions, setDealOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [inputValue, setInputValue] = useState('');
    const pageSize = 6;
    const { post, get } = useApiManager();

    const fetchAndSetData = async (pageNum, inputText) => {
        try {
            setLoading(true);

            const payload = {
                pageNumber: pageNum,
                pageSize,
                filters: [
                    {
                        fieldName: 'dealName', // Filter field
                        filterOperator: 2, // Example: "contains" operator
                        fieldValue: inputText || '', // Use inputValue for filtering
                        fieldType: 1, // Example: string field type
                    },
                ],
                sortField: '', // Optional: Add sort field if needed
                sortDescending: false, // Optional: Set sorting direction
            };

            const response = await post('/deals/paged', payload);

            const options = response.items.map(deal => ({
                label: deal.dealName || `Deal #${deal.id}`,
                value: deal.id,
            }));

            setDealOptions(prev =>
                pageNum === 1 ? options : [...prev, ...options] // Reset or append options based on page
            );
            setTotalCount(response.totalCount);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when the component mounts or input value changes
    useEffect(() => {
        if (inputValue.length >= 2) {
            fetchAndSetData(1, inputValue); // Fetch with updated input value
        } else if (inputValue === '') {
            fetchAndSetData(1, ''); // Clear filter when input is cleared
        }
    }, [inputValue]);

    // Fetch single deal by ID
    useEffect(() => {
        if (dealId > 0 && shouldFetchSingleDeal) {
            const fetchDealById = async () => {
                try {
                    setLoading(true);
                    const response = await get(`/deals/${dealId}`);
                    const dealOption = {
                        label: response.dealName || `Deal #${response.id}`,
                        value: response.id,
                    };

                    // Add the fetched deal to options
                    setDealOptions((prev) => {
                        // Check if the deal already exists in the options
                        const dealExists = prev.some(option => option.value === dealOption.value);

                        // If it doesn't exist, add it; otherwise, return the current options
                        if (!dealExists) {
                            return [dealOption, ...prev];
                        }
                        return prev;
                    });


                    // Set the selected option
                    setSelectedOption(dealOption);
                } catch (error) {
                    console.error('Error fetching deal by ID:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchDealById();
        }
    }, [dealId, shouldFetchSingleDeal]);

    const handleInputChange = (newValue) => {
        setInputValue(newValue);
        setPageNumber(1);
    };

    const handleScrollToBottom = () => {
        const totalFetched = dealOptions.length;
        if (totalFetched < totalCount && !loading) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            fetchAndSetData(nextPage, inputValue);
        }
    };

    const customStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            height: '56px',
            padding: '0px 8px',
            borderRadius: '4px',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 10,
            backgroundColor: '#fff',
            borderRadius: '4px',
        }),
    };

    return (
        <div style={{ width: '100%' }}>
            <Select
                isLoading={loading}
                isClearable
                options={dealOptions}
                value={selectedOption} // Use the explicitly managed selected option
                onChange={(selectedOption) => {
                    setDealId(selectedOption ? selectedOption.value : null);
                    setSelectedOption(selectedOption); // Update the selected option
                }}
                onInputChange={handleInputChange}
                placeholder="Select Deal"
                styles={customStyles}
                maxMenuHeight={200}
                onMenuScrollToBottom={handleScrollToBottom} // Load more data on scroll
                noOptionsMessage={() => (loading ? 'Loading...' : 'No options available')}
            />
        </div>
    );
};
export default DealsAutoComplete;

